import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, unref, type Ref, type ComputedRef } from 'vue'
import { apiClient } from '@/api/client'

// Types
export interface Project {
  id: number
  name: string
  slug: string
  description?: string
  config: {
    theme: string
    defaultLanguage: string
    languages: string[]
    siteUrl: string
  }
  status: 'active' | 'archived'
  pages?: Page[]
  createdAt: string
  updatedAt: string
}

export interface Page {
  id: number
  title: string
  slug: string
  layoutType: string
  publishedVersionId?: number
  updatedAt: string
}

interface PaginatedResponse<T> {
  items: T[]
  pagination: {
    total: number
    page: number
    limit: number
    pages: number
  }
}

interface CreateProjectData {
  name: string
  description?: string
  config: {
    theme: string
    siteUrl?: string
    defaultLanguage: string
    languages: string[]
  }
}

// Query keys
export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: (filters: string) => [...projectKeys.lists(), { filters }] as const,
  details: () => [...projectKeys.all, 'detail'] as const,
  detail: (id: number) => [...projectKeys.details(), id] as const,
  bySlug: (slug: string) => [...projectKeys.all, 'slug', slug] as const,
}

// API functions (response envelope unwrapped by interceptor in api/client.ts)
const fetchProjects = async (): Promise<PaginatedResponse<Project>> => {
  const response = await apiClient.getProjects()
  return response.data as PaginatedResponse<Project>
}

const fetchProject = async (id: number): Promise<Project> => {
  const response = await apiClient.getProjectById(id.toString())
  return response.data as Project
}

const fetchProjectBySlug = async (slug: string): Promise<Project> => {
  const response = await apiClient.getProjectBySlug(slug)
  return response.data as Project
}

const createProject = async (projectData: CreateProjectData): Promise<Project> => {
  const response = await apiClient.createProject(projectData)
  return response.data as Project
}

const deleteProject = async (id: number): Promise<void> => {
  await apiClient.deleteProject(id.toString())
}

// Query hooks
export const useProjectsQuery = () => {
  return useQuery({
    queryKey: projectKeys.lists(),
    queryFn: fetchProjects,
    select: (data) => data.items, // Extract items from paginated response
  })
}

export const useProjectQuery = (id: number | Ref<number> | ComputedRef<number>) => {
  return useQuery({
    queryKey: computed(() => projectKeys.detail(unref(id))),
    queryFn: () => fetchProject(unref(id)),
    enabled: computed(() => !!unref(id)),
  })
}

export const useProjectBySlugQuery = (slug: string) => {
  return useQuery({
    queryKey: projectKeys.bySlug(slug),
    queryFn: () => fetchProjectBySlug(slug),
    enabled: !!slug,
  })
}

// Mutation hooks
export const useCreateProjectMutation = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: createProject,
    onSuccess: (newProject) => {
      // Invalidate and refetch projects list
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() })
      
      // Add the new project to the cache
      queryClient.setQueryData(projectKeys.detail(newProject.id), newProject)
    },
  })
}

export const useDeleteProjectMutation = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: deleteProject,
    onSuccess: (_, deletedId) => {
      // Remove project from the list cache
      queryClient.setQueryData(projectKeys.lists(), (old: PaginatedResponse<Project> | undefined) => {
        if (!old) return old
        return {
          ...old,
          items: old.items.filter(project => project.id !== deletedId)
        }
      })
      
      // Remove project detail from cache
      queryClient.removeQueries({ queryKey: projectKeys.detail(deletedId) })
    },
  })
}